import React, { useEffect, useState } from "react";
import { Smartphone, X } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { isInstalledPWA, wasPWAEverInstalled } from "@/utils/pwa";

const DISMISS_KEY = "lupuslogix_switch_to_app_banner_dismissed";
const SEEN_COUNT_KEY = "lupuslogix_switch_to_app_banner_seen_count";
const MAX_SIGHTINGS = 3;

/**
 * Sticky banner shown ONLY in a mobile browser tab AFTER the user has
 * installed the PWA on this device. Reminds them to use the home screen icon
 * so push notifications and reminders don't split across two surfaces.
 *
 * Visibility rules (deliberately conservative — this banner was too noisy):
 *  - Never in the native shell or the installed PWA
 *  - Mobile / touch devices only (a desktop tab can't open the phone app)
 *  - Dismissal is permanent (localStorage, not sessionStorage)
 *  - Auto-retires after MAX_SIGHTINGS displays even without a dismissal
 */
export const BrowserAfterInstallBanner: React.FC = () => {
  const [dismissed, setDismissed] = useState(true);
  const [showHow, setShowHow] = useState(false);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    if (isInstalledPWA()) return;
    if (!wasPWAEverInstalled()) return;

    // Mobile/touch only — irrelevant on desktop browsers.
    const isTouchMobile =
      typeof window !== "undefined" &&
      window.matchMedia?.("(max-width: 767px), (pointer: coarse)").matches;
    if (!isTouchMobile) return;

    try {
      if (localStorage.getItem(DISMISS_KEY) === "true") return;
      const seen = Number(localStorage.getItem(SEEN_COUNT_KEY) ?? "0");
      if (seen >= MAX_SIGHTINGS) return;
      localStorage.setItem(SEEN_COUNT_KEY, String(seen + 1));
    } catch { /* ignore */ }

    setDismissed(false);
  }, []);

  if (dismissed) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "true"); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div className="sticky top-0 z-50 w-full bg-primary/95 text-primary-foreground shadow-md">
      <div className="mx-auto flex max-w-6xl items-center gap-2 px-3 py-2 text-sm">
        <Smartphone className="h-4 w-4 shrink-0" />
        <div className="flex-1 leading-tight">
          <span>
            Open LupusLogix from your <strong>app icon</strong> for reminders.
          </span>{" "}
          <button
            type="button"
            onClick={() => setShowHow((v) => !v)}
            className="underline underline-offset-2 opacity-90"
            aria-expanded={showHow}
          >
            {showHow ? "Hide" : "How?"}
          </button>
          {showHow && (
            <p className="mt-1 text-xs opacity-90">
              Close this tab and tap the LupusLogix icon on your home screen. Can't find it?
              Open your app drawer, long-press <strong>LupusLogix</strong>, then tap
              "Add to Home screen." Push reminders and Google Calendar sync only run inside
              the installed app.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="rounded p-1 hover:bg-primary-foreground/10"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default BrowserAfterInstallBanner;